import {
    useState, useRef, useEffect, useMemo,
} from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';

import Mic from '@mui/icons-material/Mic';
import InfoIcon from '@mui/icons-material/Info';
import { marked } from 'marked';
import sanitizeHtml from 'sanitize-html';
import GetAppIcon from '@mui/icons-material/GetApp';
import {
    ClickAwayListener,
    MenuItem, MenuList, Paper, Popper,
} from '@mui/material';

import { App } from 'BMapsSrc/BMapsApp';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { UserActions } from 'BMapsCmds';
import { Loader } from 'BMapsSrc/Loader';
import { captureAndTranscribe } from 'BMapsSrc/util/speech_utils';
import { DialogSubmitButton, DialogCancelButton } from './common/DialogActionButtons';
import { WebServices } from '../WebServices';
import { interpretText, resumeInterpretText, workflowProposedEvent } from '../cmds/gibbsDispatch';
import ClarificationChips from './common/ClarificationChips';
import { WorkingIndicator, downloadData } from './ui_utils';
import { loginUrlForMapCase } from '../connection_utils';
import BMapsTooltip from './BMapsTooltip';

const BotName = 'Gibbs';

/**
 * What to offer in an empty chat.
 *
 * Every one is a canonical phrase a DETERMINISTIC tier recognises -- the hit-discovery,
 * dock-and-score, rescore, screening and campaign shortcuts -- so clicking one exercises a path
 * that cannot hallucinate, and the list doubles as an honest statement of what the app does
 * well. Phrasings that only the LLM tier would catch are deliberately absent.
 */
const STARTER_PROMPTS = [
    { label: 'Run hit discovery', detail: 'Fragment-based: grow new compounds from a fragment map' },
    { label: 'Dock and score these compounds', detail: 'Evaluate what is already loaded' },
    { label: 'Rescore these poses', detail: 'Interaction energy, GNINA or GiFE — no re-docking' },
    { label: 'Run a hit to lead campaign', detail: 'Propose a multi-step workflow to review' },
    { label: 'Screen my library', detail: 'Score compounds against a panel of protein targets' },
    { label: 'Open the jobs panel', detail: 'Folding, compound, screening or free-energy jobs' },
];

export function ChatBot({ loggedIn, initialMessages: initialMsgs=[] }) {
    const initialMessages = useMemo(
        () => initialMsgs.map((msg, i) => ({ ...msg, id: i })),
        [initialMsgs]
    );
    const [input, setInput] = useState('');
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const [messages, setMessages] = useState(initialMessages);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [recording, setRecording] = useState(false);

    useEffect(() => {
        if (messagesEndRef?.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [messages]);

    useEffect(() => {
        if (initialMessages.length > 0) {
            (async () => {
                try {
                    setIsLoading(true);
                    await submitMessages(initialMessages);
                } catch (sendError) {
                    console.error('Error making chat agent request:', sendError);
                    setError(sendError);
                } finally {
                    setIsLoading(false);
                }
            })();
        }
    }, [initialMessages]); // eslint-disable-line react-hooks/exhaustive-deps

    const myStructure = App.Workspace && App.Workspace.getLoadedProtein();
    const loginUrl = loginUrlForMapCase(myStructure);

    const addBotMessage = (content, idIn, extra) => {
        setMessages((oldMessages) => {
            const id = idIn ?? oldMessages.length;
            const newMessage = {
                role: 'assistant', content, id, ...extra,
            };
            return [...oldMessages, newMessage];
        });
    };

    // The planner is lab-only for now. Without it, chat keeps the conversational route it has.
    const usePlanner = !!Loader.AllowLabFeatures;

    // The clarification waiting for an answer, if any. The next message answers it.
    const pendingClarification = useRef(null);

    /**
     * Put the planner's question to the user, with its options as things to CLICK.
     *
     * The options used to be flattened into a markdown bullet list, so the only way to answer
     * was to retype a label exactly -- "Human Kinases - Test 3 (BRAF, p38, JAK3)" and the like.
     * They ride the message now and render as chips (ui/common/ClarificationChips).
     */
    const askClarification = (clarification, id) => {
        pendingClarification.current = clarification;
        const question = clarification.question || 'Which did you mean?';
        addBotMessage(question, id, { clarification });
    };

    /** The option the user named, or what they typed when none matches. */
    const answerFrom = (clarification, message) => {
        const said = message.trim().toLowerCase();
        const matched = (clarification.options || []).find(
            (o) => (o.label || '').toLowerCase() === said
                || String(o.value?.decision || '').toLowerCase() === said,
        );
        return matched ? matched.value : message;
    };

    /**
     * Show what the planner came back with, and say whether it answered. A 'chat' tagged 'llm'
     * is not the planner's own answer, so the conversational route takes it instead; so does
     * anything the planner could not turn into a plan.
     */
    const showPlannerResult = (result, id) => {
        switch (result.type) {
            case 'chat':
                if (result.chatSource === 'llm') return false;
                addBotMessage(result.message, id);
                return true;
            case 'clarification':
                askClarification(result.clarification, id);
                return true;
            case 'plan': {
                const execution = result.execution || {};
                const errors = execution.errors ?? (execution.error ? [execution.error] : []);
                if (errors.length > 0) addBotMessage(errors.join('\n'), id);
                // The dispatcher composes this from the RESULTS (gibbsDispatch.planNarration),
                // so the chemist hears what the callee reported rather than a list of slugs.
                else addBotMessage(execution.narration || 'Done.', id);
                return true;
            }
            case 'workflow': {
                // A multi-step plan goes to the Workflow Checker rather than running here. The
                // pane owns review, parameter edits, the autonomy level and the start button;
                // publishing is all the chat turn does. The topic name comes from the dispatcher
                // seam so this file names no path inside the Gibbs tree.
                workflowProposedEvent()
                    .then((proposed) => {
                        EventBroker.publish(proposed, {
                            plan: result.plan,
                            request: result.request,
                        });
                        // Same route Help uses to show itself (HelpPanel.toggleHelpMenu).
                        UserActions.OpenSidePanel('right', { pageId: 'workflow' });
                    })
                    .catch((e) => console.error('Could not open the Workflow Checker', e));
                const n = (result.plan?.actions || []).length;
                addBotMessage(
                    `I've put a ${n}-step workflow in the Workflow Checker on the right. `
                    + 'Review the steps and parameters there, then start it when it looks right.',
                    id,
                );
                return true;
            }
            case 'cancelled':
                return true;
            default:
                return false;
        }
    };

    /**
     * The planner's turn: answer an outstanding clarification, or plan the message afresh.
     * False means it did not answer and the conversational route should -- because the planner
     * is off, because it declined, or because it failed. The caller does not distinguish them.
     */
    const handleWithPlanner = async (message, id) => {
        if (!usePlanner) return false;
        try {
            const pending = pendingClarification.current;
            pendingClarification.current = null;
            // A question only takes the reply that answers it. An unmet-precondition question
            // has nothing missing and is answered only by one of its options, so treating any
            // later message as its answer both re-asked it forever and, once the precondition
            // was met from the GUI, dispatched the stale action for an unrelated message.
            const answer = pending && answerFrom(pending, message);
            const answersIt = !!pending && (answer !== message
                || pending.context?.missing?.length > 0
                || pending.context?.cascade === 'destructive_confirm');
            const result = answersIt
                ? await resumeInterpretText(pending.context, answer)
                : await interpretText(message);
            const answered = showPlannerResult(result, id);
            if (answered) setError(null);
            return answered;
        } catch (plannerError) {
            console.error('Planner failed; answering with chat', plannerError);
            return false;
        }
    };

    const handleSend = async (message=input) => {
        // With the planner on, only allow one turn at a time. How send is prevented during a turn:
        // * Send button: disabled below
        // * Enter key: here
        // * Voice: here, but see BP2-2857 for a stale state issue
        if (usePlanner && isLoading) return;
        try {
            if (message.trim()) {
                setIsLoading(true);
                const userMessage = { role: 'user', content: message, id: messages.length };
                const updatedMessages = [...messages, userMessage];
                setMessages(updatedMessages);
                setInput('');
                if (await handleWithPlanner(message, updatedMessages.length)) return;
                await submitMessages(updatedMessages);
            }
        } catch (sendError) {
            console.error('Error making chat agent request:', sendError);
            setError(sendError);
        } finally {
            setIsLoading(false);
        }
    };

    const submitMessages = async (sendingMessages) => {
        const data = await WebServices.startWsRequestJson(
            WebServices.CHAT_URL, { messages: sendingMessages }
        );
        const responseData = JSON.parse(data);
        if (responseData.error === '') {
            setError(null);
            addBotMessage(responseData.response, sendingMessages.length);
        } else {
            console.error(`ChatBot error: ${responseData.error}`);
            setError(responseData.error);
        }
    };

    const handleInputChange = (e) => {
        setInput(e.target.value);
    };

    const handleEnterSubmit = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleClearChat = () => {
        pendingClarification.current = null;
        setError(null);
        setMessages([]);
    };

    const handleRegenerate = () => {
        const lastMessage = messages[messages.length - 1];
        const userText = lastMessage.content;
        setInput(userText);
        setError(null);
    };

    function displayChatMessage(inputText) {
        const renderer = new marked.Renderer();
        function convertLink(href, title, text) {
            const link = marked.Renderer.prototype.link.call(this, href, title, text);
            // Our ExternalLink also includes a rel="noreferrer" attribute, which excludes
            // the referrer (BMaps) from the request headers.
            // I chose not to add that here. If we decide to add it, we'll need to pass
            // an allowedAttributes option to sanitizeHtml, so rel isn't filtered out.
            return link.replace('<a ', '<a target="_blank" ');
        }
        renderer.link = convertLink;
        marked.setOptions({ renderer });
        return (
            <div
                dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(marked.parse(inputText), {
                        allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img']),
                        allowedAttributes: {
                            ...sanitizeHtml.defaults.allowedAttributes,
                            img: ['src', 'alt', 'title'],
                        },
                        allowedSchemes: ['https'],
                    }),
                }}
            />
        );
    }

    async function captureAndSend() {
        setRecording(true);
        const recorder = await captureAndTranscribe({
            setMediaRecorder,
            handleTranscription: async (words) => {
                if (words != null && words.length > 0) {
                    setInput(words);
                    handleSend(words);
                } else {
                    addBotMessage("If you just said something, I couldn't make it out. Please try again.");
                }
            },
        });
        recorder.onstop = () => setRecording(false);
        setMediaRecorder(recorder);
    }

    async function stopRecording() {
        mediaRecorder.stop();
    }

    return (
        <div style={{
            padding: '1rem',
            paddingTop: '0.5rem',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: '300px',
            flexGrow: 1,
            borderTop: '2px solid lightgray',
        }}
        >
            <Typography style={{ fontWeight: 'bold', fontSize: 'smaller' }}>
                Chat with
                {' '}
                {BotName}
                , our AI assistant:
            </Typography>
            <Typography style={{ fontSize: 'small', marginBottom: '0.5rem' }}>
                Please be aware responses from
                {' '}
                {BotName}
                {' '}
                may not always be accurate.
                Use discretion when interpreting results.
            </Typography>
            <div style={{
                flexGrow: '1',
                overflow: 'auto',
                padding: '0.5rem',
                borderTop: '1px solid lightgray',
                borderLeft: '1px solid lightgray',
                borderRight: '1px solid lightgray',
                borderBottom: 'none',
                borderRadius: '4px 4px 0 0',
                background: 'whitesmoke',
            }}
            >
                {messages.length === 0 && usePlanner && (
                    // An empty chat said nothing about what Gibbs can do, so the first message
                    // was a guess at the vocabulary. These are the deterministic tiers'
                    // canonical phrasings, so each one is answered by a shortcut rather than
                    // by the model -- what you click is what the app is actually good at.
                    <Box sx={{ opacity: 0.85 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Try one of these, or just describe what you want:
                        </Typography>
                        <ClarificationChips
                            options={STARTER_PROMPTS}
                            onSelect={(text) => handleSend(text)}
                        />
                    </Box>
                )}
                {messages.map((message, messageI) => (
                    <Box
                        key={`message_${messageI.toString()}`}
                        ref={message.id === messages.length - 1 ? messagesEndRef : null}
                        style={{
                            marginBottom: '2px',
                            padding: '2px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
                        }}
                    >
                        <strong>{message.role === 'user' ? 'You: ' : `${BotName}: `}</strong>
                        <Box sx={{
                            border: '1px solid lightgray',
                            borderRadius: '.3rem',
                            maxWidth: '90%',
                            wordWrap: 'break-word',
                            background: message.role === 'user' ? 'white' : 'mintcream',
                            boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2)',
                            '& li': {
                                marginLeft: '1rem',
                            },
                            '& div': {
                                padding: '.3rem',
                            },
                            '& pre': {
                                whiteSpace: 'pre-wrap',
                            },
                            '& img': {
                                maxWidth: '100%',
                                height: 'auto',
                            },
                        }}
                        >
                            {displayChatMessage(message.content)}
                            {message.clarification && (
                                <ClarificationChips
                                    options={message.clarification.options}
                                    // Clicking sends the label as if typed, so the choice lands
                                    // in the transcript and both answer routes stay identical.
                                    onSelect={(text) => handleSend(text)}
                                    // Only the question still awaiting an answer is live. An old
                                    // question's chips stay visible as a record of what was
                                    // offered, but clicking one would answer a cascade that has
                                    // already moved on.
                                    disabled={pendingClarification.current
                                        !== message.clarification}
                                />
                            )}
                        </Box>
                    </Box>
                ))}
                <Box>
                    {isLoading && (
                        <Box>
                            <WorkingIndicator />
                        </Box>
                    )}
                </Box>
                {error && <ChatBotErrorDisplay error={error} onTryAgain={handleRegenerate} />}
                <Box>
                    {!loggedIn && (
                        <Typography style={{ fontStyle: 'italic', fontWeight: 'bold', fontSize: 'smaller' }}>
                            Please
                            {' '}
                            <a href={loginUrl}>Log in</a>
                            {' '}
                            to interact with
                            {' '}
                            {BotName}
                            .
                        </Typography>
                    )}
                </Box>
            </div>
            <TextField
                style={{
                    marginBottom: '0.5rem',
                    width: '100%',
                    flexGrow: 1,
                    justifyContent: 'flex-end',
                    borderLeft: '1px solid lightgray',
                    borderRight: '1px solid lightgray',
                    borderRadius: '0 0 4px 4px',
                    background: 'whitesmoke',
                }}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleEnterSubmit}
                placeholder="Enter message..."
                multiline
                maxRows={6}
                size="small"
                disabled={!loggedIn}
                slotProps={{
                    input: {
                        sx: {
                            background: 'white',
                        },
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={recording ? stopRecording : captureAndSend}
                                    style={{
                                        color: recording ? 'green' : undefined,
                                        padding: '2px 4px',
                                        height: 'fit-content',
                                        width: 'fit-content',
                                        display: 'flex',
                                    }}
                                    title={recording ? 'Disable voice control' : 'Enable voice control'}
                                    disabled={!loggedIn}
                                >
                                    <Mic />
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <DialogSubmitButton
                    onClick={() => handleSend()}
                    disabled={!loggedIn || (usePlanner && isLoading)}
                >
                    Send
                </DialogSubmitButton>
                <DialogCancelButton
                    onClick={handleClearChat}
                    disabled={!loggedIn}
                >
                    Clear chat
                </DialogCancelButton>
                <ChatMenu messages={messages} />
            </div>
        </div>
    );
}

function ChatBotErrorDisplay({ error, onTryAgain }) {
    const errorRef = useRef(null);
    useEffect(() => {
        if (errorRef?.current) {
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [error]);

    return (
        <Box ref={errorRef}>
            <strong>
                {`${BotName}: `}
            </strong>
            <BMapsTooltip title={`Error detail: ${error}`}>
                <div style={{ fontStyle: 'italic', color: 'red' }}>
                    Something went wrong...
                    {' '}
                    <InfoIcon fontSize="small" style={{ verticalAlign: 'middle' }} />
                </div>
            </BMapsTooltip>
            <DialogCancelButton style={{ paddingTop: 0, paddingBottom: 0 }} fontSize="smaller" type="button" onClick={onTryAgain}>Try again</DialogCancelButton>
        </Box>
    );
}

function ChatMenu({ messages }) {
    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);
    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {messages.length > 0 && (
                <>
                    <BMapsTooltip title="Download chat history">
                        <IconButton size="small" ref={menuRef} onClick={() => setOpen(!open)}>
                            <GetAppIcon />
                        </IconButton>
                    </BMapsTooltip>
                    <Popper
                        open={open}
                        anchorEl={menuRef.current}
                        placement="bottom-start"
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={() => setOpen(false)}>
                                <MenuList>
                                    <MenuItem
                                        sx={{ justifyContent: 'center' }}
                                        onClick={
                                    () => {
                                        const stateString = JSON.stringify(
                                            { chatMessages: messages }
                                        );
                                        downloadData(getChatFileName('json'), stateString, 'application/json');
                                        setOpen(false);
                                    }
                                }
                                    >
                                        JSON
                                    </MenuItem>
                                    <MenuItem
                                        sx={{ justifyContent: 'end' }}
                                        onClick={
                                        () => {
                                            const stateString = messages.map((message) => `${message.role}:\n${message.content}`).join('\n\n');
                                            downloadData(getChatFileName('md'), stateString, 'text/plain');
                                            setOpen(false);
                                        }
                                    }
                                    >
                                        Markdown
                                    </MenuItem>
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Popper>
                </>
            )}
        </Box>
    );
}

function getChatFileName(format) {
    return `BMaps_GibbsChat_${formatDate()}.${format}`;
}

/**
 * @param {Date?} dateIn
 * @returns {string}
 */
function formatDate(dateIn) {
    const date = dateIn || new Date();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    // const hours = date.getHours();
    // const minutes = date.getMinutes();
    // const seconds = date.getSeconds();
    return `${year}-${month}-${day}`;
}
